import { MapPin, Clock, Truck, Mail, Phone } from "lucide-react";
import { SITE, IMG } from "@/data/site";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const AREA_IDS = ["los-angeles", "orange-county", "inland-empire"];

export const Logistics = () => {
  const { t } = useLang();
  const l = t.logistics;
  return (
  <section className="py-24 lg:py-32" data-testid="logistics-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeader eyebrow={l.eyebrow} title={l.title} text={l.text} />
          <Reveal delay={0.15} className="mt-10 space-y-5">
            {[
              [Clock, l.hours, t.site.hours, "hours"],
              [Truck, l.delivery, t.site.delivery, "delivery"],
              [MapPin, `${SITE.address}`, SITE.city, "address"],
            ].map(([Icon, h, s, id]) => (
              <div key={id} className="flex items-start gap-4" data-testid={`logistics-${id}`}>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--green)]/10 text-[var(--green)]">
                  <Icon size={18} />
                </span>
                <div>
                  <p className="font-semibold">{h}</p>
                  <p className="font-mono-label mt-0.5 text-[10.5px] text-[var(--muted)]">{s}</p>
                </div>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.25} className="mt-10 flex flex-wrap gap-3">
            <a href={`mailto:${SITE.email}`} className="btn-primary" data-testid="logistics-email-btn">
              <Mail size={16} /> {l.email}
            </a>
            <a href={SITE.phoneHref} className="btn-ghost" data-testid="logistics-phone-btn">
              <Phone size={16} /> {SITE.phone}
            </a>
          </Reveal>
        </div>

        <Stagger className="grid gap-4 lg:col-span-7 lg:grid-cols-3" stagger={0.12}>
          {l.areas.map((a, i) => (
            <Item key={AREA_IDS[i]} className={`${i === 0 ? "lg:col-span-3" : ""}`}>
              <div
                className={`group relative h-full overflow-hidden rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1 ${
                  i === 0 ? "bg-[var(--green)] text-white" : "border border-[var(--line)] bg-white"
                }`}
                data-testid={`service-area-${AREA_IDS[i]}`}
              >
                <p className={`font-mono-label text-[10px] ${i === 0 ? "text-white/70" : "text-[var(--muted)]"}`}>{l.route} 0{i + 1}</p>
                <h3 className="font-display mt-6 text-3xl font-bold leading-none lg:text-4xl">{a.name}</h3>
                <p className={`mt-4 text-sm ${i === 0 ? "text-white/80" : "text-[var(--ink-2)]"}`}>{a.note}</p>
                <span
                  className={`absolute -bottom-10 -right-10 h-36 w-36 rounded-full transition-transform duration-700 group-hover:scale-150 ${
                    i === 0 ? "bg-[var(--lime)]/40" : i === 1 ? "bg-[var(--gold)]/30" : "bg-[var(--red)]/20"
                  }`}
                />
              </div>
            </Item>
          ))}
          <Item className="lg:col-span-3">
            <div className="img-zoom group relative aspect-[16/7] overflow-hidden rounded-3xl bg-[var(--ink)]" data-testid="fleet-photo">
              <img src={IMG.trucks} alt="Sebastian's Produce refrigerated trucks" className="h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between gap-4 text-white">
                <p className="max-w-md text-sm font-medium leading-snug">{l.fleetCaption}</p>
                <span className="hidden whitespace-nowrap rounded-full bg-white/15 px-3 py-1.5 font-mono-label text-[10px] backdrop-blur sm:block">{t.site.delivery}</span>
              </div>
            </div>
          </Item>
        </Stagger>
      </div>
    </div>
  </section>
  );
};
